import type { Panel, PanelRow } from "./panels.ts";

/**
 * Modul-bredd per DIN-komponent. Domänen känner inte själv till
 * kortkods-katalogen (den ligger i @electro-planner/components) — men
 * vi behöver ändå göra layout-beräkningar här för att hålla projektionen
 * i ett paket. Därför exporteras `computePanelLayout` som tar emot en
 * `modulesForRow`-lookup: UI:t skickar in en funktion som slår i
 * katalogen, tester kan skicka in en stub.
 *
 * Varje rad upptar minst 1 modul. Rader utan komponent-val förutsätts
 * också uppta 1 modul (så layouten reserverar plats).
 */

export type RailSlot = {
  row: PanelRow;
  modules: number;      // bredd (1 för dvärg, 2 för JFA, 4 för 4-pol JFB …)
  startPosition: number; // 1-baserad kolumn på skenan
};

export type RailLayout = {
  railNumber: number;        // 1-baserad
  capacity: number;          // moduler per skena enligt panel.modulesPerRail
  used: number;              // summa av modules för rader på denna skena
  overflow: boolean;         // used > capacity
  slots: RailSlot[];
};

export type PanelLayoutReport = {
  rails: RailLayout[];
  /** Rader som inte har railNumber satt — visas som "ej placerade". */
  unplaced: PanelRow[];
  /** Global varning om någon rail överstiger kapacitet. */
  anyOverflow: boolean;
};

export const DEFAULT_MODULES_PER_RAIL = 12;
export const DEFAULT_RAIL_COUNT = 2;

export function computePanelLayout(
  panel: Panel | undefined,
  rows: ReadonlyArray<PanelRow>,
  modulesForRow: (row: PanelRow) => number,
): PanelLayoutReport {
  const railCount = panel?.railCount ?? DEFAULT_RAIL_COUNT;
  const capacity = panel?.modulesPerRail ?? DEFAULT_MODULES_PER_RAIL;

  const rails: RailLayout[] = [];
  for (let i = 1; i <= railCount; i++) {
    rails.push({
      railNumber: i,
      capacity,
      used: 0,
      overflow: false,
      slots: [],
    });
  }

  const unplaced: PanelRow[] = [];
  const sorted = [...rows].sort((a, b) => a.order - b.order);

  for (const row of sorted) {
    const modules = Math.max(1, modulesForRow(row));
    const rn = row.railNumber;
    if (rn === undefined || rn < 1 || rn > railCount) {
      unplaced.push(row);
      continue;
    }
    const rail = rails[rn - 1]!;
    const startPosition = rail.used + 1;
    rail.slots.push({ row, modules, startPosition });
    rail.used += modules;
  }

  let anyOverflow = false;
  for (const r of rails) {
    r.overflow = r.used > r.capacity;
    if (r.overflow) anyOverflow = true;
  }

  return { rails, unplaced, anyOverflow };
}
